import { AlertTriangle, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminModalShell from "../../../../components/admin/AdminModalShell";
import contractsV2Service from "../../../../services/contractsV2Service";
import {
  Contract,
  ContractReferenceData,
  StoredContractReferenceData,
} from "../../../../types/contract";
import {
  REFERENCE_DATA_STALE_DAYS,
  REFRESH_EXCLUSION_MESSAGES,
  getReferenceDataError,
  getRefreshEligibility,
} from "../../../../utils/contractReferenceData";
import { formatUtcDate } from "../../../../utils/dateUtils";
import { formatMarketCap, formatVolume } from "../../../../utils/numberUtils";

interface ContractReferenceDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract;
  /** Re-read the contract row so the table shows the merged result, not a prediction. */
  onSaved: () => void | Promise<void>;
}

const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const inputClassName =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white dark:placeholder-gray-500";

const labelClassName = "block text-sm font-medium text-gray-700 dark:text-gray-300";

const currentValueClassName = "mt-0.5 font-mono text-xs text-gray-500 dark:text-gray-400";

/** Shown where a reference-data value has never been written. ASCII only, per the repo rules. */
const NO_STORED_VALUE = "-";

/** Only include a numeric field when it parses and is non-zero -- a zero is discarded server-side. */
const parseNumericInput = (raw: string): { value?: number; error?: string } => {
  const trimmed = raw.trim();
  if (trimmed === "") return {};

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return { error: "Must be a number" };
  if (parsed === 0) return { error: "Zero is treated as no data and would be ignored" };
  if (parsed < 0) return { error: "Must be greater than zero" };
  return { value: parsed };
};

const ContractReferenceDataModal: React.FC<ContractReferenceDataModalProps> = ({
  isOpen,
  onClose,
  contract,
  onSaved,
}) => {
  const [stored, setStored] = useState<StoredContractReferenceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [marketCapInput, setMarketCapInput] = useState("");
  const [volumeInput, setVolumeInput] = useState("");
  const [latestSplitDtInput, setLatestSplitDtInput] = useState("");
  const [splitRatioInput, setSplitRatioInput] = useState("");

  // Guards the stored-reference-data read against out-of-order resolution: the modal is
  // keyed to a user-selected row, so a slower earlier request must never overwrite a
  // newer one's result.
  const loadReqRef = useRef(0);

  const { conId, symbol } = contract;

  const resetInputs = useCallback(() => {
    setMarketCapInput("");
    setVolumeInput("");
    setLatestSplitDtInput("");
    setSplitRatioInput("");
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const reqId = ++loadReqRef.current;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setStored(null);
    resetInputs();

    contractsV2Service
      .getStoredReferenceData(conId)
      .then((result) => {
        if (reqId !== loadReqRef.current) return;
        setStored(result);
      })
      .catch((err) => {
        if (reqId !== loadReqRef.current) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load reference data");
      })
      .finally(() => {
        if (reqId !== loadReqRef.current) return;
        setLoading(false);
      });
  }, [isOpen, conId, resetInputs]);

  const referenceData = stored?.referenceData ?? null;
  const receivedAtUtc = stored?.referenceDataReceivedAtUtc ?? null;
  const fetchError = getReferenceDataError({ referenceData });
  const eligibility = getRefreshEligibility({
    secType: contract.secType,
    primaryExchange: contract.primaryExchange,
    referenceDataReceivedAtUtc: receivedAtUtc,
  });

  const marketCapResult = parseNumericInput(marketCapInput);
  const volumeResult = parseNumericInput(volumeInput);
  const splitRatioValue = splitRatioInput.trim();
  const splitDateValue = latestSplitDtInput.trim();

  const hasEdits =
    marketCapResult.value !== undefined ||
    volumeResult.value !== undefined ||
    splitDateValue !== "" ||
    splitRatioValue !== "";
  const hasInputErrors = Boolean(marketCapResult.error || volumeResult.error);
  const canSave = hasEdits && !hasInputErrors && !loading && !saving && !loadError;

  const handleSave = async () => {
    if (!canSave) return;

    // Only changed fields are sent: the API merges, so an omitted key means
    // "leave unchanged". Nothing can be cleared through this endpoint.
    const payload: ContractReferenceData = {};
    if (marketCapResult.value !== undefined) payload.marketCap = marketCapResult.value;
    if (volumeResult.value !== undefined) payload.volume = volumeResult.value;
    if (splitDateValue !== "") payload.latestSplitDt = splitDateValue;
    if (splitRatioValue !== "") payload.splitRatio = splitRatioValue;

    setSaving(true);
    setSaveError(null);
    try {
      const result = await contractsV2Service.putStoredReferenceData(conId, payload);
      // Ignore any in-flight read: this response is the newest state for this contract.
      loadReqRef.current += 1;
      setStored(result);
      resetInputs();
      await onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save reference data");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const storedMarketCap = referenceData?.marketCap;
  const storedVolume = referenceData?.volume;
  const storedSplitDt = referenceData?.latestSplitDt;
  const storedSplitRatio = referenceData?.splitRatio;

  return (
    <AdminModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={`Refresh Reference Data: ${symbol}`}
      description={<span className="font-mono">Con ID {conId}</span>}
      maxWidthClassName="max-w-2xl"
      closeDisabled={saving}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Corrections
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading reference data...
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {loadError}
        </div>
      ) : (
        <div className="space-y-5">
          {/* Status */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Last update attempt
            </div>
            <div className="mt-1 text-sm text-gray-900 dark:text-white">
              {receivedAtUtc ? formatUtcDate(receivedAtUtc, DATE_TIME_FORMAT) : "Never fetched"}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This is when reference data was last written, not when it was last good -- the
              refresh job stamps it on failed fetches too.
            </p>

            <div className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Next automatic refresh
            </div>
            {eligibility.scheduled ? (
              <>
                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                  {eligibility.eligibleAtUtc
                    ? `Eligible after ${formatUtcDate(eligibility.eligibleAtUtc, DATE_TIME_FORMAT)}`
                    : "Eligible now"}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Based on a {REFERENCE_DATA_STALE_DAYS}-day staleness window. The job is
                  budget-bound and works a prioritized queue, so a contract can be picked up
                  later than this -- it is the earliest it becomes eligible, not a scheduled time.
                </p>
              </>
            ) : (
              <div className="mt-1 text-sm text-gray-900 dark:text-white">
                {REFRESH_EXCLUSION_MESSAGES[eligibility.reason]}
              </div>
            )}
          </div>

          {/* Last attempt failed */}
          {fetchError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <span className="font-semibold">Last fetch failed.</span> The values below are
                  from an older successful write, not from the last attempt.
                  <div className="mt-1 break-words font-mono text-xs">{fetchError}</div>
                </div>
              </div>
            </div>
          )}

          {/* Corrections */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Correct values</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Leave a field blank to keep its stored value. These fields can be corrected but
              never cleared here -- an empty or zero value is ignored by design. Clearing a value
              is a deliberate manual database operation.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="ref-data-market-cap" className={labelClassName}>
                  Market Cap
                </label>
                <div className={currentValueClassName}>
                  Current:{" "}
                  {storedMarketCap !== null && storedMarketCap !== undefined
                    ? formatMarketCap(storedMarketCap as number | string)
                    : NO_STORED_VALUE}
                </div>
                <input
                  id="ref-data-market-cap"
                  type="number"
                  min="0"
                  step="any"
                  value={marketCapInput}
                  onChange={(e) => setMarketCapInput(e.target.value)}
                  placeholder="Unchanged"
                  className={`${inputClassName} mt-1.5`}
                />
                {marketCapResult.error && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-500">
                    {marketCapResult.error}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="ref-data-volume" className={labelClassName}>
                  Volume (10-day avg shares)
                </label>
                <div className={currentValueClassName}>
                  Current:{" "}
                  {storedVolume !== null && storedVolume !== undefined
                    ? formatVolume(storedVolume as number | string)
                    : NO_STORED_VALUE}
                </div>
                <input
                  id="ref-data-volume"
                  type="number"
                  min="0"
                  step="any"
                  value={volumeInput}
                  onChange={(e) => setVolumeInput(e.target.value)}
                  placeholder="Unchanged"
                  className={`${inputClassName} mt-1.5`}
                />
                {volumeResult.error && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-500">
                    {volumeResult.error}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="ref-data-split-date" className={labelClassName}>
                  Split Date
                </label>
                <div className={currentValueClassName}>
                  Current: {storedSplitDt ? formatUtcDate(storedSplitDt) : NO_STORED_VALUE}
                </div>
                <input
                  id="ref-data-split-date"
                  type="date"
                  value={latestSplitDtInput}
                  onChange={(e) => setLatestSplitDtInput(e.target.value)}
                  className={`${inputClassName} mt-1.5`}
                />
              </div>

              <div>
                <label htmlFor="ref-data-split-ratio" className={labelClassName}>
                  Split Ratio
                </label>
                <div className={currentValueClassName}>
                  Current: {storedSplitRatio || NO_STORED_VALUE}
                </div>
                <input
                  id="ref-data-split-ratio"
                  type="text"
                  value={splitRatioInput}
                  onChange={(e) => setSplitRatioInput(e.target.value)}
                  placeholder="Unchanged"
                  className={`${inputClassName} mt-1.5`}
                />
              </div>
            </div>
          </div>

          {/* Consequences of saving */}
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-500">
            <p className="font-semibold">Saving also:</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>
                Resets the staleness clock. The next automatic refresh moves out by a full{" "}
                {REFERENCE_DATA_STALE_DAYS}-day window from now.
              </li>
              <li>
                Attempts to recompute Avg $ Volume (volume x price x FX-to-USD) whenever volume
                is saved. If no usable price or FX rate can be resolved the recompute is skipped
                and the previous value stays -- the save still succeeds.
              </li>
              <li>Clears the recorded fetch error, if there is one.</li>
            </ul>
          </div>

          {saveError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {saveError}
            </div>
          )}
        </div>
      )}
    </AdminModalShell>
  );
};

export default ContractReferenceDataModal;
